/**
 * @file src/components/HighPriorityIssueModal.tsx
 * @fileoverview Modal that warns about open high-priority GitHub issues.
 */
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import { useI18n } from '../i18n';
import {
  fetchGithubRoadmap,
  GithubRoadmapIssue,
  isOpenHighPriorityIssue,
} from '../utils/githubRoadmap';
import { useHighPriorityIssueStore } from '../store/highPriorityIssueStore';

const STORAGE_KEY = 'highPriorityIssuesDismissed';

const getIssueSignature = (issues: GithubRoadmapIssue[]): string =>
  issues
    .map(issue => (issue.number ? `#${issue.number}` : issue.title))
    .join('|');

const HighPriorityIssueModal: React.FC = () => {
  const { t } = useI18n();
  const issues = useHighPriorityIssueStore(state => state.issues);
  const setIssues = useHighPriorityIssueStore(state => state.setIssues);
  const isOpen = useHighPriorityIssueStore(state => state.isOpen);
  const open = useHighPriorityIssueStore(state => state.open);
  const close = useHighPriorityIssueStore(state => state.close);

  const displayIssues = useMemo(
    () =>
      issues.map(issue => ({
        ...issue,
        title: issue.title
      })),
    [issues]
  );

  const loadIssues = useCallback(async (forceRefresh = false) => {
    try {
      const data = await fetchGithubRoadmap({ forceRefresh });
      const openHigh = (data.issues || []).filter(isOpenHighPriorityIssue);
      setIssues(openHigh);
      if (openHigh.length === 0) {
        close();
        return;
      }
      const signature = getIssueSignature(openHigh);
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed !== signature) {
        open();
      }
    } catch {
      // Silent fail: no modal when data is unavailable.
    }
  }, [close, open, setIssues]);

  useEffect(() => {
    void loadIssues();

    const handleRoadmapUpdate = () => {
      void loadIssues(true);
    };
    window.addEventListener('roadmap-cache-updated', handleRoadmapUpdate);
    return () => window.removeEventListener('roadmap-cache-updated', handleRoadmapUpdate);
  }, [loadIssues]);

  const handleClose = () => {
    const signature = getIssueSignature(issues);
    if (signature) {
      localStorage.setItem(STORAGE_KEY, signature);
    }
    close();
  };

  if (issues.length === 0) {
    return null;
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-error-50 text-error-600">
                      <AlertTriangle size={20} />
                    </span>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                        {t('high_priority_issue_title')}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-600">
                        {t('high_priority_issue_body')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-md text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  {displayIssues.map(issue => (
                    <div
                      key={issue.number ?? issue.title}
                      className="flex items-center justify-between gap-3 rounded-lg border border-error-100 bg-error-50 px-3 py-2 text-sm text-error-700"
                    >
                      <span className="font-medium">{issue.title}</span>
                      {issue.html_url && (
                        <a
                          href={issue.html_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-error-700 hover:text-error-800"
                        >
                          {t('high_priority_issue_view')}
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-lg bg-error-600 px-4 py-2 text-sm font-semibold text-white hover:bg-error-700"
                  >
                    {t('high_priority_issue_dismiss')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default HighPriorityIssueModal;
